import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import AdminCreatorsTab from "@/pages/AdminCreatorsTab";
import AdminBroadcastTab from "@/pages/AdminBroadcastTab";
import { toast } from "sonner";
import { Pin, Lock, Trash2, RotateCcw, Shield, Flag, ExternalLink, UserX, Users as UsersIcon, BarChart3, Store, Check, X, MapPin, Sparkles, Crown, Megaphone, ShieldAlert, BadgeCheck, TestTube2, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Admin() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [banTarget, setBanTarget] = useState(null); // user object
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState("permanent");
  const [deleteTarget, setDeleteTarget] = useState(null); // step 1: first confirm modal
  const [deleteConfirm2, setDeleteConfirm2] = useState(""); // step 2: type "DELETE" to confirm
  const [analytics, setAnalytics] = useState(null);
  const [sellerApps, setSellerApps] = useState([]);
  const [sellerAppsFilter, setSellerAppsFilter] = useState("pending");
  const [crumbPrompt, setCrumbPrompt] = useState("");
  const [defaultCrumbPrompt, setDefaultCrumbPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [appeals, setAppeals] = useState([]);
  const [appealsFilter, setAppealsFilter] = useState("open");
  const [mktKind, setMktKind] = useState("logo");
  const [mktPrompt, setMktPrompt] = useState("");
  const [mktDefault, setMktDefault] = useState("");
  const [mktSaving, setMktSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false || user.role !== "admin") {
      toast.error("Admin only");
      nav("/");
    }
  }, [user, loading, nav]);

  const loadPosts = async () => {
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const loadReports = async () => {
    try {
      const { data } = await api.get("/admin/reports", { params: { status: "open" } });
      setReports(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadUsers = async (q = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: q ? { search: q } : {} });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleBan = async (u) => {
    if (u.is_banned) {
      if (!window.confirm(`Unban @${u.username}?`)) return;
      setBusy(true);
      try {
        await api.post(`/admin/users/${u.id}/unban`);
        toast.success("User unbanned");
        loadUsers(userSearch);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setBusy(false);
      return;
    }
    // Open ban dialog
    setBanTarget(u);
    setBanReason("");
    setBanDuration("permanent");
  };

  const submitBan = async () => {
    if (!banTarget) return;
    setBusy(true);
    try {
      const payload = { reason: banReason || null };
      if (banDuration !== "permanent") {
        payload.duration_days = parseInt(banDuration, 10);
      }
      await api.patch(`/admin/users/${banTarget.id}/ban`, payload);
      toast.success(banDuration === "permanent"
        ? `@${banTarget.username} banned permanently`
        : `@${banTarget.username} banned for ${banDuration} days`);
      setBanTarget(null);
      loadUsers(userSearch);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm2 !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`@${deleteTarget.username} permanently deleted`);
      setDeleteTarget(null);
      setDeleteConfirm2("");
      loadUsers(userSearch);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/admin/analytics/rules");
      setAnalytics(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadSellerApps = async (status = sellerAppsFilter) => {
    try {
      const { data } = await api.get("/admin/seller-applications", { params: { status } });
      setSellerApps(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveSellerApp = async (app) => {
    if (!window.confirm(`Approve @${app.username} as a seller? "${app.business_name}" shop will go live immediately.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/seller-applications/${app.id}/approve`);
      toast.success(`@${app.username} is now a seller`);
      loadSellerApps();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const rejectSellerApp = async (app) => {
    if (!window.confirm(`Reject @${app.username}'s application? They can re-apply with more detail.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/seller-applications/${app.id}/reject`);
      toast.success(`Rejected @${app.username}`);
      loadSellerApps();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const togglePremium = async (u) => {
    const next = !u.is_premium;
    if (!window.confirm(`${next ? "Grant" : "Revoke"} premium for @${u.username}?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${u.id}/premium`, { is_premium: next });
      toast.success(`${next ? "Granted" : "Revoked"} premium`);
      loadUsers(userSearch);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const toggleTester = async (u) => {
    const next = !u.is_tester;
    if (!window.confirm(`${next ? "Promote" : "Demote"} @${u.username} ${next ? "to" : "from"} Tester? Testers get free access to every feature and can view the private Testers Chat forum.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/tester`);
      toast.success(data.is_tester ? `@${u.username} is now a Tester` : `@${u.username} removed from Testers`);
      loadUsers(userSearch);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const loadCrumbPrompt = async () => {
    try {
      const { data } = await api.get("/admin/crumb/prompt");
      setCrumbPrompt(data.prompt);
      setDefaultCrumbPrompt(data.default);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCrumbPrompt = async () => {
    if (!crumbPrompt.trim()) { toast.error("Prompt cannot be empty"); return; }
    setSavingPrompt(true);
    try {
      await api.patch("/admin/crumb/prompt", { prompt: crumbPrompt });
      toast.success("Prompt saved — next analyses will use it");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingPrompt(false);
  };

  const loadAppeals = async (status = appealsFilter) => {
    try {
      const { data } = await api.get("/admin/appeals", { params: { status } });
      setAppeals(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveAppeal = async (appeal) => {
    if (!window.confirm(`Unban @${appeal.username}? Their appeal will be marked approved.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${appeal.user_id}/unban`);
      toast.success(`@${appeal.username} unbanned`);
      loadAppeals();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const rejectAppeal = async (appeal) => {
    if (!window.confirm(`Reject appeal from @${appeal.username}? Ban stays in place.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/appeals/${appeal.id}/reject`);
      toast.success("Appeal rejected");
      loadAppeals();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const loadMktPrompt = async (kind = mktKind) => {
    try {
      const { data } = await api.get(`/admin/marketing/prompt/${kind}`);
      setMktPrompt(data.prompt);
      setMktDefault(data.default);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveMktPrompt = async () => {
    if (!mktPrompt.trim()) { toast.error("Prompt cannot be empty"); return; }
    setMktSaving(true);
    try {
      await api.patch(`/admin/marketing/prompt/${mktKind}`, { prompt: mktPrompt });
      toast.success("Marketing prompt saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setMktSaving(false);
  };

  const toggleVerified = async (u) => {
    const next = !u.seller_profile?.verified;
    if (!window.confirm(`${next ? "Verify" : "Un-verify"} @${u.username}'s shop?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/sellers/${u.id}/verify`, { verified: next });
      toast.success(next ? "Shop verified" : "Verification removed");
      loadUsers(userSearch);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  useEffect(() => {
    if (user?.role === "admin") {
      loadPosts();
      loadReports();
      loadUsers();
      loadAnalytics();
      loadSellerApps();
      loadCrumbPrompt();
    }
     
  }, [user]);

  const togglePin = async (p) => {
    setBusy(true);
    try {
      await api.patch(`/admin/posts/${p.id}/pin`, { value: !p.is_pinned });
      toast.success(p.is_pinned ? "Unpinned" : "Pinned to top");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const toggleLock = async (p) => {
    setBusy(true);
    try {
      await api.patch(`/admin/posts/${p.id}/lock`, { value: !p.is_locked });
      toast.success(p.is_locked ? "Unlocked" : "Locked");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const softDelete = async (p) => {
    if (!window.confirm(`Soft-delete "${p.title}"? You can restore it later.`)) return;
    setBusy(true);
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Post soft-deleted");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const restore = async (p) => {
    setBusy(true);
    try {
      await api.post(`/admin/posts/${p.id}/restore`);
      toast.success("Post restored");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const hardDelete = async (p) => {
    if (!window.confirm(`PERMANENTLY delete "${p.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/posts/${p.id}/hard`);
      toast.success("Post permanently deleted");
      loadPosts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const resolveReport = async (r) => {
    setBusy(true);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      toast.success("Report resolved");
      loadReports();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (loading || !user || user.role !== "admin") {
    return (
      <>
        <Header />
        <div className="p-10 text-[#857163]">Checking permissions…</div>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#FF7E67]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163]">Moderation</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">Admin Dashboard</h1>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setTab("posts")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "posts" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-posts"
          >
            Posts ({posts.length})
          </button>
          <button
            onClick={() => setTab("reports")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "reports" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-reports"
          >
            <Flag className="w-3.5 h-3.5 inline mr-1" /> Reports ({reports.length})
          </button>
          <button
            onClick={() => setTab("users")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "users" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-users"
          >
            <UsersIcon className="w-3.5 h-3.5 inline mr-1" /> Users ({users.length})
          </button>
          <button
            onClick={() => setTab("analytics")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "analytics" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-analytics"
          >
            <BarChart3 className="w-3.5 h-3.5 inline mr-1" /> Analytics
          </button>
          <button
            onClick={() => setTab("sellers")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "sellers" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-sellers"
          >
            <Store className="w-3.5 h-3.5 inline mr-1" /> Seller Apps
            {sellerApps.filter((a) => a.status === "pending").length > 0 && sellerAppsFilter === "pending" && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF7E67] text-white text-[10px] font-bold">
                {sellerApps.filter((a) => a.status === "pending").length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("analyzer")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "analyzer" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-analyzer"
          >
            <Sparkles className="w-3.5 h-3.5 inline mr-1" style={{ color: tab === "analyzer" ? "#F4A83B" : "#F4A83B" }} /> Analyzer Prompt
          </button>
          <button
            onClick={() => { setTab("marketing"); loadMktPrompt(mktKind); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "marketing" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-marketing"
          >
            <Megaphone className="w-3.5 h-3.5 inline mr-1" style={{ color: "#8E7CC3" }} /> Marketing Prompts
          </button>
          <button
            onClick={() => { setTab("appeals"); loadAppeals(); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "appeals" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-appeals"
          >
            <ShieldAlert className="w-3.5 h-3.5 inline mr-1" style={{ color: "#C2185B" }} /> Ban Appeals
            {appeals.filter((a) => a.status === "open").length > 0 && appealsFilter === "open" && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C2185B] text-white text-[10px] font-bold">
                {appeals.filter((a) => a.status === "open").length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("creators")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "creators" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-creators"
          >
            <Sparkles className="w-3.5 h-3.5 inline mr-1" style={{ color: "#F4A83B" }} /> Creators
          </button>
          <button
            onClick={() => setTab("broadcast")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              tab === "broadcast" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
            }`}
            data-testid="admin-tab-broadcast"
          >
            📣 Broadcast
          </button>
        </div>

        {tab === "creators" ? (
          <AdminCreatorsTab />
        ) : tab === "broadcast" ? (
          <AdminBroadcastTab />
        ) : tab === "posts" ? (
          <div className="cp-card overflow-hidden" data-testid="admin-posts-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#EBDCD1] text-xs uppercase tracking-wide text-[#857163] font-medium">
              <div className="col-span-6">Post</div>
              <div className="col-span-2">Author</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {posts.length === 0 ? (
              <div className="p-8 text-center text-[#857163]">No posts yet</div>
            ) : (
              posts.map((p) => (
                <div key={p.id} className={`grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-[#EBDCD1]/50 ${p.is_deleted ? "opacity-50" : ""}`} data-testid={`admin-post-row-${p.id}`}>
                  <div className="col-span-6">
                    <div className="flex items-center gap-2">
                      {p.is_pinned && <Pin className="w-3.5 h-3.5 text-[#FF7E67]" />}
                      {p.is_locked && <Lock className="w-3.5 h-3.5 text-[#857163]" />}
                      <Link to={`/p/${p.id}`} className="font-semibold text-[#3B2C24] hover:text-[#FF7E67] truncate">
                        {p.title}
                      </Link>
                      <Link to={`/p/${p.id}`} className="text-[#857163] hover:text-[#3B2C24]"><ExternalLink className="w-3.5 h-3.5" /></Link>
                    </div>
                    <div className="text-xs text-[#857163] mt-0.5">
                      {p.category} · {(() => { try { return formatDistanceToNow(new Date(p.created_at), { addSuffix: true }); } catch { return ""; } })()} · {p.like_count} likes · {p.comment_count} comments
                    </div>
                  </div>
                  <div className="col-span-2 text-sm text-[#3B2C24] truncate">@{p.author.username}</div>
                  <div className="col-span-1">
                    {p.is_deleted ? (
                      <span className="text-xs text-red-500 font-medium">deleted</span>
                    ) : (
                      <span className="text-xs text-[#8EAC50] font-medium">live</span>
                    )}
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-1 flex-wrap">
                    {!p.is_deleted ? (
                      <>
                        <button onClick={() => togglePin(p)} disabled={busy} title={p.is_pinned ? "Unpin" : "Pin"} className={`p-1.5 rounded-full transition ${p.is_pinned ? "bg-[#FF7E67]/15 text-[#FF7E67]" : "hover:bg-[#F9EFE6] text-[#857163]"}`} data-testid={`admin-pin-${p.id}`}>
                          <Pin className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleLock(p)} disabled={busy} title={p.is_locked ? "Unlock" : "Lock"} className={`p-1.5 rounded-full transition ${p.is_locked ? "bg-[#3B2C24]/10 text-[#3B2C24]" : "hover:bg-[#F9EFE6] text-[#857163]"}`} data-testid={`admin-lock-${p.id}`}>
                          <Lock className="w-4 h-4" />
                        </button>
                        <button onClick={() => softDelete(p)} disabled={busy} title="Soft delete" className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition" data-testid={`admin-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => restore(p)} disabled={busy} title="Restore" className="p-1.5 rounded-full hover:bg-[#8EAC50]/10 text-[#8EAC50] transition" data-testid={`admin-restore-${p.id}`}>
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button onClick={() => hardDelete(p)} disabled={busy} title="Delete permanently" className="p-1.5 rounded-full hover:bg-red-100 text-red-600 transition" data-testid={`admin-hard-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : tab === "reports" ? (
          <div className="cp-card p-0" data-testid="admin-reports-list">
            {reports.length === 0 ? (
              <div className="p-8 text-center text-[#857163]">No open reports 🎉</div>
            ) : (
              reports.map((r) => (
                <div key={r.id} className="p-4 border-b border-[#EBDCD1]/50" data-testid={`admin-report-${r.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {r.kind === "message" ? (
                        <>
                          <div className="font-semibold text-[#3B2C24] flex items-center gap-2">
                            <span className="cp-tag" style={{ background: "rgba(59,44,36,0.10)" }}>DM</span>
                            Reported message
                          </div>
                          <div className="text-sm text-[#3B2C24] mt-1 bg-[#F9EFE6]/60 rounded-lg px-2 py-1.5 italic">
                            &ldquo;{r.message_body}&rdquo;
                          </div>
                        </>
                      ) : (
                        <Link to={`/p/${r.post_id}`} className="font-semibold text-[#3B2C24] hover:text-[#FF7E67]">
                          {r.post_title}
                        </Link>
                      )}
                      <div className="text-sm text-[#3B2C24] mt-1">
                        <span className="cp-tag mr-2">{r.reason_label || r.reason}</span>
                        {r.details && <span className="text-[#857163]">— {r.details}</span>}
                      </div>
                      <div className="text-xs text-[#857163] mt-1">
                        Reported by @{r.reporter_username} · {(() => { try { return formatDistanceToNow(new Date(r.created_at), { addSuffix: true }); } catch { return ""; } })()}
                      </div>
                    </div>
                    <button
                      onClick={() => resolveReport(r)}
                      disabled={busy}
                      className="cp-btn-primary text-sm shrink-0"
                      data-testid={`admin-resolve-${r.id}`}
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : tab === "users" ? (
          <div className="cp-card overflow-hidden" data-testid="admin-users-table">
            <div className="p-3 border-b border-[#EBDCD1]">
              <input
                className="cp-input"
                placeholder="Search by username or email…"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); loadUsers(e.target.value); }}
                data-testid="admin-users-search"
              />
            </div>
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#EBDCD1] text-xs uppercase tracking-wide text-[#857163] font-medium">
              <div className="col-span-3">User</div>
              <div className="col-span-2">Email</div>
              <div className="col-span-1">Role</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Trial</div>
              <div className="col-span-2">Last Active</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {users.length === 0 ? (
              <div className="p-8 text-center text-[#857163]">No users found</div>
            ) : (
              users.map((u) => (
                <div key={u.id} className={`grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-[#EBDCD1]/50 ${u.is_banned ? "opacity-60" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    <Link to={`/u/${u.username}`} className="font-semibold text-[#3B2C24] hover:text-[#FF7E67] truncate">
                      @{u.username}
                    </Link>
                  </div>
                  <div className="col-span-2 text-sm text-[#857163] truncate">{u.email}</div>
                  <div className="col-span-1 text-xs">
                    {u.role === "admin" ? (
                      <span className="cp-tag" style={{ background: "rgba(255, 126, 103, 0.15)" }}>admin</span>
                    ) : (
                      <span className="text-[#857163]">user</span>
                    )}
                  </div>
                  <div className="col-span-1">
                    {u.is_banned ? (
                      <div className="text-xs text-red-500 font-medium">
                        banned
                        {u.banned_until && (
                          <div className="text-[10px] text-[#857163] font-normal">
                            until {(() => { try { return formatDistanceToNow(new Date(u.banned_until), { addSuffix: true }); } catch { return u.banned_until; } })()}
                          </div>
                        )}
                        {u.ban_reason && (
                          <div className="text-[10px] text-[#857163] font-normal truncate" title={u.ban_reason}>
                            {u.ban_reason}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[#8EAC50] font-medium">active</span>
                    )}
                  </div>
                  <div className="col-span-1 text-xs" data-testid={`admin-trial-days-${u.id}`}>
                    {(() => {
                      if (u.is_premium) return <span className="text-[#857163]">—</span>;
                      if (!u.trial_ends_at) return <span className="text-[#857163]">—</span>;
                      try {
                        const msLeft = new Date(u.trial_ends_at).getTime() - Date.now();
                        const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
                        if (daysLeft <= 0) return <span className="text-red-500 font-semibold">expired</span>;
                        return <span className={daysLeft <= 3 ? "text-red-500 font-semibold" : "text-[#3B2C24]"}>{daysLeft}d</span>;
                      } catch { return <span className="text-[#857163]">—</span>; }
                    })()}
                  </div>
                  <div className="col-span-2 text-xs text-[#857163]" data-testid={`admin-last-active-${u.id}`}>
                    {u.last_login_at ? (() => {
                      try {
                        const d = new Date(u.last_login_at);
                        const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                        const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                        return <><div className="text-[#3B2C24]">{date}</div><div className="text-[10px]">{time}</div></>;
                      } catch { return "—"; }
                    })() : <span className="italic">never</span>}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <Link
                      to={`/admin/users/${u.id}`}
                      title="View this user's info & help them fix issues"
                      className="px-2 py-1.5 rounded-full text-xs font-medium bg-[#F9EFE6] border border-[#EBDCD1] text-[#3B2C24] hover:bg-white transition"
                      data-testid={`admin-view-${u.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 inline" />
                    </Link>
                    {u.is_seller && (
                      <button
                        onClick={() => toggleVerified(u)}
                        disabled={busy}
                        title={u.seller_profile?.verified ? "Remove verification" : "Verify shop"}
                        className={`px-2 py-1.5 rounded-full text-xs font-medium transition ${u.seller_profile?.verified ? "text-white" : "bg-white border border-[#EBDCD1] text-[#857163] hover:bg-[#F9EFE6]"}`}
                        style={u.seller_profile?.verified ? { background: "#2F80ED" } : {}}
                        data-testid={`admin-verify-${u.id}`}
                      >
                        <BadgeCheck className="w-3.5 h-3.5 inline" />
                      </button>
                    )}
                    <button
                      onClick={() => togglePremium(u)}
                      disabled={busy}
                      title={u.is_premium ? "Revoke premium" : "Grant premium"}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition ${
                        u.is_premium
                          ? "text-white"
                          : "bg-white border border-[#EBDCD1] text-[#857163] hover:bg-[#F9EFE6]"
                      }`}
                      style={u.is_premium ? { background: "linear-gradient(135deg, #F4A83B, #E8842B)" } : {}}
                      data-testid={`admin-premium-${u.id}`}
                    >
                      <Crown className="w-3.5 h-3.5 inline mr-0.5" />
                      {u.is_premium ? "Premium" : "Free"}
                    </button>
                    {u.role !== "admin" && (
                      <button
                        onClick={() => toggleTester(u)}
                        disabled={busy}
                        title={u.is_tester ? "Remove from Testers" : "Promote to Tester"}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition ${
                          u.is_tester
                            ? "text-white"
                            : "bg-white border border-[#EBDCD1] text-[#857163] hover:bg-[#F9EFE6]"
                        }`}
                        style={u.is_tester ? { background: "#2F80ED" } : {}}
                        data-testid={`admin-tester-${u.id}`}
                      >
                        <TestTube2 className="w-3.5 h-3.5 inline mr-0.5" />
                        {u.is_tester ? "Tester" : "Tester?"}
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <>
                        <button
                          onClick={() => toggleBan(u)}
                          disabled={busy}
                          title={u.is_banned ? "Unban" : "Ban"}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                            u.is_banned
                              ? "bg-[#8EAC50]/15 text-[#8EAC50] hover:bg-[#8EAC50]/25"
                              : "bg-red-50 text-red-600 hover:bg-red-100"
                          }`}
                          data-testid={`admin-ban-${u.id}`}
                        >
                          <UserX className="w-3.5 h-3.5 inline mr-1" />
                          {u.is_banned ? "Unban" : "Ban"}
                        </button>
                        <button
                          onClick={() => { setDeleteTarget(u); setDeleteConfirm2(""); }}
                          disabled={busy}
                          title="Permanently delete user"
                          className="px-2.5 py-1.5 rounded-full text-xs font-medium transition text-red-700 bg-red-100 hover:bg-red-200 border border-red-200"
                          data-testid={`admin-delete-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : tab === "analytics" ? (
          <div data-testid="admin-analytics">
            {!analytics ? (
              <div className="cp-card p-8 text-center text-[#857163]">Loading analytics…</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="cp-card p-5">
                    <div className="text-xs uppercase tracking-widest text-[#857163]">Current version</div>
                    <div className="font-heading text-4xl text-[#3B2C24] mt-1">v{analytics.current_version}</div>
                  </div>
                  <div className="cp-card p-5">
                    <div className="text-xs uppercase tracking-widest text-[#857163]">Accepted current rules</div>
                    <div className="font-heading text-4xl text-[#8EAC50] mt-1">{analytics.users_accepted_current}</div>
                    <div className="text-xs text-[#857163] mt-1">of {analytics.total_users} total users</div>
                  </div>
                  <div className="cp-card p-5">
                    <div className="text-xs uppercase tracking-widest text-[#857163]">Pending acceptance</div>
                    <div className="font-heading text-4xl text-[#FF7E67] mt-1">{analytics.users_pending_current}</div>
                    <div className="text-xs text-[#857163] mt-1">users haven&apos;t accepted v{analytics.current_version}</div>
                  </div>
                </div>

                <div className="cp-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#EBDCD1] font-semibold text-[#3B2C24]">
                    Per-version funnel
                  </div>
                  <div className="grid grid-cols-6 gap-2 px-4 py-2 border-b border-[#EBDCD1] text-xs uppercase tracking-wide text-[#857163] font-medium">
                    <div>Version</div>
                    <div>Shown</div>
                    <div>Accepted</div>
                    <div>Drop-off</div>
                    <div className="col-span-2">Acceptance rate</div>
                  </div>
                  {analytics.per_version.length === 0 ? (
                    <div className="p-6 text-center text-[#857163] text-sm">
                      No events yet — rules modal analytics will appear here after users see it.
                    </div>
                  ) : analytics.per_version.map((row) => (
                    <div key={row.version} className="grid grid-cols-6 gap-2 items-center px-4 py-3 border-b border-[#EBDCD1]/50" data-testid={`analytics-row-v${row.version}`}>
                      <div className="text-sm text-[#3B2C24] font-medium">
                        v{row.version}{" "}
                        {row.is_current && <span className="cp-tag ml-1">current</span>}
                      </div>
                      <div className="text-sm text-[#3B2C24]">{row.shown_users}</div>
                      <div className="text-sm text-[#8EAC50] font-medium">{row.accepted_users}</div>
                      <div className="text-sm text-red-500 font-medium">{row.drop_off_users}</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#EBDCD1] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#8EAC50] rounded-full"
                            style={{ width: `${row.acceptance_rate_pct}%` }}
                          />
                        </div>
                        <div className="text-xs text-[#3B2C24] font-medium min-w-[42px] text-right">
                          {row.acceptance_rate_pct}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : tab === "sellers" ? (
          <div data-testid="admin-sellers">
            <div className="flex gap-2 mb-4 flex-wrap">
              {["pending", "approved", "rejected", "all"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setSellerAppsFilter(s); loadSellerApps(s); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    sellerAppsFilter === s
                      ? "bg-[#FF7E67] border-[#FF7E67] text-white"
                      : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                  }`}
                  data-testid={`seller-filter-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {sellerApps.length === 0 ? (
              <div className="cp-card p-8 text-center text-[#857163]">
                No {sellerAppsFilter === "all" ? "" : sellerAppsFilter} applications
              </div>
            ) : (
              <div className="space-y-3">
                {sellerApps.map((app) => (
                  <div key={app.id} className="cp-card p-5" data-testid={`seller-app-${app.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Store className="w-4 h-4 text-[#FF7E67] shrink-0" />
                          <div className="font-heading text-xl text-[#3B2C24] font-semibold">
                            {app.business_name}
                          </div>
                          {app.status === "pending" && (
                            <span className="cp-tag" style={{ background: "rgba(255,208,127,0.35)" }}>Pending</span>
                          )}
                          {app.status === "approved" && (
                            <span className="cp-tag" style={{ background: "rgba(142,172,80,0.20)", color: "#5F7A2E" }}>Approved</span>
                          )}
                          {app.status === "rejected" && (
                            <span className="cp-tag" style={{ background: "rgba(220,38,38,0.15)", color: "#B91C1C" }}>Rejected</span>
                          )}
                        </div>
                        <div className="text-sm text-[#857163] mt-1 flex items-center gap-3 flex-wrap">
                          <Link to={`/u/${app.username}`} className="hover:text-[#FF7E67]">@{app.username}</Link>
                          {app.contact_email && (
                            <a href={`mailto:${app.contact_email}`} className="inline-flex items-center gap-1 hover:text-[#FF7E67]">
                              <span aria-hidden>✉</span> {app.contact_email}
                            </a>
                          )}
                          {app.city && (
                            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {app.city}</span>
                          )}
                          <span>{(() => { try { return formatDistanceToNow(new Date(app.created_at), { addSuffix: true }); } catch { return ""; } })()}</span>
                        </div>
                        {app.tagline && (
                          <div className="mt-2 text-sm text-[#3B2C24] italic">&ldquo;{app.tagline}&rdquo;</div>
                        )}
                        <p className="mt-3 text-sm text-[#3B2C24] whitespace-pre-wrap leading-relaxed max-w-3xl">
                          {app.about}
                        </p>
                      </div>
                      {app.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => approveSellerApp(app)}
                            disabled={busy}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-white transition disabled:opacity-50"
                            style={{ background: "#8EAC50" }}
                            data-testid={`seller-approve-${app.id}`}
                          >
                            <Check className="w-4 h-4 inline mr-1" /> Approve
                          </button>
                          <button
                            onClick={() => rejectSellerApp(app)}
                            disabled={busy}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition disabled:opacity-50"
                            data-testid={`seller-reject-${app.id}`}
                          >
                            <X className="w-4 h-4 inline mr-1" /> Reject
                          </button>
                        </div>
                      )}
                      {app.status === "approved" && (
                        <Link
                          to={`/shop/${app.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cp-btn-ghost text-sm shrink-0 inline-flex items-center gap-1.5"
                          data-testid={`seller-visit-${app.id}`}
                        >
                          Visit shop <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "analyzer" ? (
          <div data-testid="admin-analyzer-prompt">
            <div className="cp-card p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <h2 className="font-heading text-2xl text-[#3B2C24] flex items-center gap-2">
                    <Sparkles className="w-5 h-5" style={{ color: "#F4A83B" }} /> AI Analyzer prompt
                  </h2>
                  <p className="text-sm text-[#857163] mt-1 max-w-2xl">
                    This system prompt is sent to Gemini 3 Flash with every bake analysis. Tune it to change the tone, structure, or focus of the AI&apos;s diagnosis.
                  </p>
                </div>
                <button
                  onClick={() => setCrumbPrompt(defaultCrumbPrompt)}
                  className="text-xs text-[#FF7E67] hover:underline shrink-0"
                  data-testid="reset-default-prompt"
                >Reset to default</button>
              </div>
              <textarea
                value={crumbPrompt}
                onChange={(e) => setCrumbPrompt(e.target.value)}
                className="cp-input min-h-[420px] font-mono text-xs leading-relaxed resize-y"
                placeholder="Loading prompt…"
                data-testid="crumb-prompt-textarea"
              />
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-[#857163]">
                  {crumbPrompt.length.toLocaleString()} / 20,000 characters
                </div>
                <button
                  onClick={saveCrumbPrompt}
                  disabled={savingPrompt || !crumbPrompt.trim()}
                  className="px-5 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-50 transition hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #F4A83B, #E8842B)" }}
                  data-testid="save-prompt-btn"
                >{savingPrompt ? "Saving…" : "Save prompt"}</button>
              </div>
              <div className="text-[11px] text-[#857163] mt-3">
                Tip: Ask the AI to return strict JSON with keys <code>confidence</code>, <code>diagnosis</code>, <code>causes</code>, <code>recommendations</code>, <code>positives</code>, <code>quality_score</code>, <code>bake_type_detected</code>. The frontend expects those exact fields.
              </div>
            </div>
          </div>
        ) : tab === "marketing" ? (
          <div data-testid="admin-marketing-prompts">
            <div className="cp-card p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <h2 className="font-heading text-2xl text-[#3B2C24] flex items-center gap-2">
                    <Megaphone className="w-5 h-5" style={{ color: "#8E7CC3" }} /> Marketing AI prompts
                  </h2>
                  <p className="text-sm text-[#857163] mt-1 max-w-2xl">
                    Edit the system prompts sent to Gemini for the Logo Maker, Social Post Generator, and Newsletter Templates tools.
                  </p>
                </div>
                <button onClick={() => setMktPrompt(mktDefault)} className="text-xs text-[#FF7E67] hover:underline shrink-0" data-testid="reset-mkt-prompt">Reset to default</button>
              </div>

              <div className="flex gap-2 mb-3 flex-wrap">
                {[
                  { key: "logo", label: "Logo Maker", color: "#8E7CC3" },
                  { key: "social", label: "Social Posts", color: "#E1306C" },
                  { key: "newsletter", label: "Newsletter", color: "#2F80ED" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setMktKind(t.key); loadMktPrompt(t.key); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${mktKind === t.key ? "text-white" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`}
                    style={mktKind === t.key ? { background: t.color, borderColor: t.color } : {}}
                    data-testid={`mkt-kind-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <textarea
                value={mktPrompt}
                onChange={(e) => setMktPrompt(e.target.value)}
                className="cp-input min-h-[380px] font-mono text-xs leading-relaxed resize-y"
                placeholder="Loading prompt…"
                data-testid="mkt-prompt-textarea"
              />
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-[#857163]">{mktPrompt.length.toLocaleString()} / 20,000 characters</div>
                <button
                  onClick={saveMktPrompt}
                  disabled={mktSaving || !mktPrompt.trim()}
                  className="px-5 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-50 transition hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #8E7CC3, #6B5FA8)" }}
                  data-testid="save-mkt-prompt-btn"
                >{mktSaving ? "Saving…" : "Save prompt"}</button>
              </div>
              <div className="text-[11px] text-[#857163] mt-3">
                Note: for Logo Maker, this is the system message. The user prompt is dynamically built from the baker&apos;s business name, style, colors, concept, and reference image.
              </div>
            </div>
          </div>
        ) : tab === "appeals" ? (
          <div data-testid="admin-appeals">
            <div className="flex gap-2 mb-4 flex-wrap">
              {["open", "approved", "rejected", "all"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setAppealsFilter(s); loadAppeals(s); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    appealsFilter === s ? "bg-[#C2185B] border-[#C2185B] text-white" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                  }`}
                  data-testid={`appeals-filter-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            {appeals.length === 0 ? (
              <div className="cp-card p-8 text-center text-[#857163]">No {appealsFilter === "all" ? "" : appealsFilter} appeals</div>
            ) : (
              <div className="space-y-3">
                {appeals.map((ap) => (
                  <div key={ap.id} className="cp-card p-5" data-testid={`appeal-${ap.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: "#C2185B" }} />
                          <Link to={`/u/${ap.username}`} className="font-heading text-xl text-[#3B2C24] font-semibold hover:text-[#FF7E67]">@{ap.username}</Link>
                          <span className="cp-tag text-[10px]" style={{ background: ap.status === "open" ? "rgba(194,24,91,0.15)" : ap.status === "approved" ? "rgba(142,172,80,0.20)" : "rgba(133,113,99,0.15)", color: ap.status === "open" ? "#C2185B" : ap.status === "approved" ? "#5F7A2E" : "#857163" }}>{ap.status}</span>
                        </div>
                        <div className="text-xs text-[#857163] mt-1 flex gap-3 flex-wrap">
                          <span>{ap.email}</span>
                          <span>{(() => { try { return formatDistanceToNow(new Date(ap.created_at), { addSuffix: true }); } catch { return ""; } })()}</span>
                        </div>
                        {ap.ban_reason && <div className="mt-2 text-xs text-[#857163]"><span className="font-semibold text-[#3B2C24]">Ban reason:</span> {ap.ban_reason}</div>}
                        <p className="mt-3 text-sm text-[#3B2C24] whitespace-pre-wrap leading-relaxed max-w-3xl bg-[#F9EFE6] rounded-2xl p-3">{ap.message}</p>
                      </div>
                      {ap.status === "open" && (
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => approveAppeal(ap)} disabled={busy} className="px-4 py-2 rounded-full text-sm font-semibold text-white transition disabled:opacity-50" style={{ background: "#8EAC50" }} data-testid={`appeal-approve-${ap.id}`}>
                            <Check className="w-4 h-4 inline mr-1" /> Unban
                          </button>
                          <button onClick={() => rejectAppeal(ap)} disabled={busy} className="px-4 py-2 rounded-full text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition disabled:opacity-50" data-testid={`appeal-reject-${ap.id}`}>
                            <X className="w-4 h-4 inline mr-1" /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div />
        )}

        {/* Ban dialog */}
        <Dialog open={!!banTarget} onOpenChange={(v) => { if (!v) setBanTarget(null); }}>
          <DialogContent className="bg-[#FFFBF5] border-[#EBDCD1] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-[#3B2C24]">
                Ban @{banTarget?.username}
              </DialogTitle>
            </DialogHeader>
            <div>
              <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Reason (optional)</label>
              <textarea
                className="cp-input min-h-[80px] resize-none text-sm"
                placeholder="e.g. Repeated spam, harassment of other users…"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                data-testid="ban-reason-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Duration</label>
              <div className="flex flex-wrap gap-2" data-testid="ban-duration-options">
                {[
                  { key: "1", label: "1 day" },
                  { key: "7", label: "7 days" },
                  { key: "30", label: "30 days" },
                  { key: "permanent", label: "Permanent" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setBanDuration(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                      banDuration === opt.key
                        ? "bg-[#FF7E67] border-[#FF7E67] text-white"
                        : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                    }`}
                    data-testid={`ban-duration-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBanTarget(null)} className="cp-btn-ghost">Cancel</button>
              <button
                onClick={submitBan}
                disabled={busy}
                className="cp-btn-primary bg-red-500 hover:bg-red-600 disabled:opacity-50"
                style={{ background: "#DC2626" }}
                data-testid="ban-submit-btn"
              >
                {busy ? "Banning…" : `Ban ${banDuration === "permanent" ? "permanently" : `for ${banDuration} days`}`}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete user — double confirm dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteConfirm2(""); } }}>
          <DialogContent data-testid="delete-user-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <ShieldAlert className="w-5 h-5" /> Permanently delete @{deleteTarget?.username}?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm text-[#3B2C24]">
              <div className="rounded-2xl bg-red-50 border border-red-200 p-3 text-red-900">
                <div className="font-semibold mb-1">This cannot be undone.</div>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Their account and login are removed</li>
                  <li>Their posts, comments, and products are soft-deleted (thread integrity preserved)</li>
                  <li>Notifications, follows, favorites, reports & appeals are purged</li>
                  <li>Direct messages they sent are anonymized to <i>[deleted user]</i></li>
                </ul>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide">
                  Type <code className="bg-[#F9EFE6] px-1.5 py-0.5 rounded text-red-700 font-bold">DELETE</code> to confirm
                </label>
                <input
                  className="cp-input mt-1"
                  value={deleteConfirm2}
                  onChange={(e) => setDeleteConfirm2(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirm2(""); }}
                className="cp-btn-ghost"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitDelete}
                disabled={busy || deleteConfirm2 !== "DELETE"}
                className="cp-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#DC2626" }}
                data-testid="delete-submit-btn"
              >
                {busy ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </>
  );
}
